import React, { useEffect, useState } from "react";
import axiosInstance from "../../../utils/axiosInstance";
import { useFormContext } from "react-hook-form";

const Downloads = ({
    selectedCompany, 
    fetchCompany,
}) => {

    const columnLabels = {
        grouped_trip_id: 'Grouped Trip', weekday: 'Weekday', bound: 'Bound', date: 'Date', time_slot: 'Time slot', picked_up_at: 'Pick up at', dropped_off_at: 'Dropped off at', passenger_name: 'Passenger name', 
        employee_number: 'Employee number', passenger_id: 'Passenger id', passenger_email: 'Passenger email', passenger_address: 'Passenger address', passenger_km: 'Passenger km', passenger_team: 'Passenger team', passenger_fare: 'Passenger fare', 
        cancellations: 'Cancellations', cancellation_time: 'Cancellation time', cancellation_period: 'Cancellation period', tier: 'Tier', short_fall: 'Short fall', total_vat: 'Total vat', note: 'Note',
        vehicle_car_rate: 'Vehicle car rate', cancelled_by: 'Cancelled by', driver_name: 'Driver name', comment: 'Comment', duration: 'Duration', trip_km: 'Trip km', rounded_trip_km: 'Rounded trip km',
        cancelled: 'Cancelled', km_accuracy: 'Km accuracy', occupancy: 'Occupancy', occupancy_actual: 'Occupancy actual', trip_reason: 'Trip reason', fleet_used: 'Fleet used', total_pax: 'Total pax',
        km0_km19: 'Km0 km19', km20_km29: 'Km20 km29', km30_km39: 'Km30 km39', km40_km49: 'Km40 km49', km50_km59: 'Km50 km59', km60_km69: 'Km60 km69', km70_km79: 'Km70 km79', km80_km89: 'Km80 km89', km90: 'km90',
        repeat_columns: 'Repeat columns', risk_flag: 'Risk flag', cancelled_by_role: 'Cancelled by role', vehicle_registration: 'Vehicle registration', company_address: 'Company address', booking_type: 'Booking Type'
    };

    const {
        watch,
        setValue,
        reset,
        formState: { isDirty }
    } = useFormContext();

    const csvColumns = watch("csv_column");
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        if (!csvColumns || Object.keys(csvColumns).length === 0) {
            const defaultValues = Object.keys(columnLabels).reduce((acc, key) => {
                acc[key] = false;
                return acc;
            }, {});
            setValue("csv_column", defaultValues, { shouldDirty: false });
        }
    }, [csvColumns, setValue]);

    const handleButtonClick = (key) => {
        setValue('csv_column', {
            ...csvColumns || {},
            [key]: !csvColumns[key]
        }, { shouldDirty: true });
    };

    const getFormattedCsvColumn = () => {
        const duplicateCsvColumn = { ...csvColumns };
        ["company_id", "created_at", "updated_at", "id"].forEach(key => delete duplicateCsvColumn[key]);
        return duplicateCsvColumn;
    };

    // Save changes
    const updateCompany = async (data) => {
        setIsLoading(true)
        try {   
            await axiosInstance({
                method: 'PATCH',
                url: `/companies/${selectedCompany.id}/csv_columns/${csvColumns.id}.json`,
                data: {
                    csv_column: getFormattedCsvColumn(),
                },
            });
            await fetchCompany();
        } catch (error) {
            console.error("API error message: ", error.message);
        } finally {
            setIsLoading(false)
        }
    };

    const handleSelectAll = () => {
        const updatedColumns = { ...csvColumns || {} };
        Object.keys(csvColumns).forEach(key => {
            if (!['id', 'company_id', 'created_at', 'updated_at'].includes(key)) {  
                updatedColumns[key] = true;
            }
        });
        setValue('csv_column', updatedColumns, { shouldDirty: true });
    };

    const handleDeselectAll = () => {
        const updatedColumns = { ...csvColumns || {} };
        Object.keys(csvColumns).forEach(key => {
            if (!['id', 'company_id', 'created_at', 'updated_at'].includes(key)) {  
                updatedColumns[key] = false;
            }
        });
        setValue('csv_column', updatedColumns, { shouldDirty: true });
    };
    
    const handleRevertChanges = async () => {
        reset();
    };

    return (
        <div className="w-full p-4 flex flex-col">

            {/* CSV Columns section */}
            <div className="csv_column flex flex-wrap px-3 md:px-0">

                <div className="w-full md:w-1/4 flex flex-col pr-5 mb-4 md:mb-0" style={{ whiteSpace: 'pre-wrap' }}>
                    <div className="w-full pr-5">
                        <h3 className="text-lg font-medium text-gray-700">CSV Columns</h3>
                        <p className="text-sm text-gray-500">Columns included in CSV downloads</p>
                    </div>
                </div>

                <div className="w-full md:w-3/4 flex flex-col">
                
                    <div className="w-full flex flex-wrap">
                        <button
                            onClick={handleSelectAll}
                            className={`p-2 mt-2 mb-2 mr-2 text-sm flex align-center justify-center relative cursor-pointer underline ${
                                isLoading ? 'cursor-wait' : ''
                            } bg-transparent text-blue-500 hover:text-blue-700`}
                        >
                            Select All
                        </button>
                        <button
                            onClick={handleDeselectAll}
                            className={`p-2 mt-2 mb-2 mr-2 text-sm flex align-center justify-center relative cursor-pointer underline ${
                                isLoading ? 'cursor-wait' : ''
                            } bg-transparent text-blue-500 hover:text-blue-700`}
                        >
                            Deselect All
                        </button>
                    </div>

                    <div className="w-full flex flex-wrap">
                        {
                            Object.keys(csvColumns)
                                .filter((key) => !['id', 'company_id', 'created_at', 'updated_at'].includes(key))
                                .map((key) => (
                                <button
                                    key={key}
                                    onClick={() => handleButtonClick(key)}
                                    className={`p-3 pr-4 pl-4 mt-2 mb-2 mr-2 text-sm flex items-center justify-center relative cursor-pointer border rounded ${
                                        isLoading ? 'cursor-wait' : ''
                                    } ${
                                        csvColumns[key]
                                            ? 'button'
                                            : 'bg-transparent text-black border-black'
                                    }  ${
                                            !csvColumns[key] ? 'hover:bg-gray-200 hover:border-black' : ''
                                        }`}
                                    disabled={isLoading}
                                >
                                    {columnLabels[key] || key}
                                </button>
                            ))
                        }
                    </div>
                </div>
            </div>

            <hr className="border border-gray-200 my-3 mb-16" />

            {/* Save and revert buttons */}
            <div className="relative pt-8">
                <div className="save_revert_buttons absolute bottom-0 right-0 flex space-x-3">
                    <button
                        className={`button p-2 text-white rounded text-sm flex align-center justify-center relative cursor-pointer ${
                            isLoading ? "bg-gray-400" : "button"
                        }`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={updateCompany}
                        disabled={isLoading}
                    >
                        {isLoading ? "Saving..." : "Save"}
                    </button>
                    <button
                        className={`p-2 text-white rounded text-sm flex items-center justify-center relative cursor-pointer bg-gray-500 hover:bg-gray-600`}
                        style={{ width: "156px", userSelect: "none" }}
                        onClick={handleRevertChanges}
                    >
                        Revert
                    </button>
                </div>
            </div>
        </div>
    );
}

export default Downloads;